import React from 'react';
import type { FileError } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import { Button, Stack, Typography } from '@mui/material';
import { UploadPdf, VyIcon } from '@vy/ui-design-system';
import { isNotEmptyArray, isNotNullOrUndefined } from '@vy/utils';
import { DROP_ZONE_CONFIG, getErrorMessage } from './import-contract-utils';
import { DROPZONE_COLORS, dropzoneContainerSx, uploadButtonSx } from './import-contract-dropzone-styles';

type ImportContractDropzoneProps = {
  onFilesSelect: (files: File[]) => void;
  onFileError: (error: FileError) => void;
  errorMessage: FileError | null;
};

export const ImportContractDropzone: React.FC<ImportContractDropzoneProps> = ({
  onFilesSelect,
  onFileError,
  errorMessage,
}) => {
  const { getRootProps, getInputProps, isDragActive, open: openFileDialog } = useDropzone({
    ...DROP_ZONE_CONFIG,
    noClick: true,
    onDrop: (acceptedFiles, fileRejections) => {
      if (isNotEmptyArray(fileRejections)) {
        const { errors } = fileRejections[0];
        onFileError(errors[0]);
        return;
      }

      onFilesSelect(acceptedFiles);
    },
  });

  const hasError = isNotNullOrUndefined(errorMessage);
  const { border, background } = DROPZONE_COLORS;

  const borderColor = isDragActive ? border.active : hasError ? border.error : border.default;
  const backgroundColor = isDragActive ? background.active : hasError ? background.error : background.default;

  return (
    <Stack
      {...getRootProps()}
      sx={{
        alignItems: "center",
        justifyContent: "center",
        borderColor: borderColor,
        borderRadius: "12px",
        ...dropzoneContainerSx,
        backgroundColor
      }}>
      <input {...getInputProps()} />
      <UploadPdf />
      <Stack
        sx={{
          alignItems: "center",
          justifyContent: "center"
        }}>
        <Typography
          variant="body1"
          sx={{
            color: "text.primary",
            fontWeight: 400,
            fontSize: 14
          }}>
          Drop contracts here
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            fontSize: 13
          }}>
          Or
        </Typography>
      </Stack>
      <Button
        variant="outlined"
        size="small"
        startIcon={<VyIcon icon="CircularUpload" />}
        onClick={openFileDialog}
        sx={uploadButtonSx}
      >
        Upload file
      </Button>
      {hasError && (
        <Typography
          variant="body2"
          sx={{
            color: "error.main",
            mt: 1.5,
            fontSize: 13
          }}>
          {getErrorMessage(errorMessage)}
        </Typography>
      )}
    </Stack>
  );
};
